import { FormEvent, useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import { AuthenticationApi } from '../../clients'
import { getConfiguration } from '../../clients/configuration'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlertWithTimeout } from '../../redux/features/alerts/alertsHelpers'
import { authenticate } from '../../redux/features/auth/authSlice'
import { useAppDispatch } from '../../redux/hooks'
import { RecaptchaService } from '../../services/recaptchaService'
import { getErrorModel } from '../../utils/getErrorModel'

interface PasswordStrength {
    score: number // 0-4
    feedback: string
    color: 'error' | 'warning' | 'success'
}

interface PasswordRequirement {
    label: string
    test: (password: string) => boolean
    met: boolean
}

export const ResetPassword = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const [password, setPassword] = useState<string>('')
    const [confirmPassword, setConfirmPassword] = useState<string>('')
    const [passwordError, setPasswordError] = useState<string>('')
    const [confirmError, setConfirmError] = useState<string>('')
    const [showPassword, setShowPassword] = useState<boolean>(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
        score: 0,
        feedback: '',
        color: 'error'
    })
    const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirement[]>([
        { label: 'At least 8 characters', test: (pwd) => pwd.length >= 8, met: false },
        { label: 'Contains at least 1 number', test: (pwd) => /[0-9]/.test(pwd), met: false },
        { label: 'Contains at least 1 special character', test: (pwd) => /[^a-zA-Z0-9]/.test(pwd), met: false },
        { label: 'Contains at least 1 uppercase letter', test: (pwd) => /[A-Z]/.test(pwd), met: false },
        { label: 'Contains at least 1 lowercase letter', test: (pwd) => /[a-z]/.test(pwd), met: false }
    ])

    const token = searchParams.get('token')

    useEffect(() => {
        if (!token) {
            const alert = new AppAlert('error', 'Invalid or missing reset token. Please request a new password reset.', true)
            addAlertWithTimeout(dispatch, alert)
            navigate('/auth/forgot-password')
        }
    }, [token, navigate, dispatch])

    const validatePasswordRequirements = (pwd: string) => {
        const updatedRequirements = passwordRequirements.map(req => ({
            ...req,
            met: req.test(pwd)
        }))
        setPasswordRequirements(updatedRequirements)
        return updatedRequirements
    }

    const validatePasswordStrength = (pwd: string): PasswordStrength => {
        if (!pwd) {
            return { score: 0, feedback: '', color: 'error' }
        }

        const requirements = validatePasswordRequirements(pwd)
        const metRequirements = requirements.filter(req => req.met).length
        const score = metRequirements

        let feedback = ''
        let color: 'error' | 'warning' | 'success' = 'error'

        if (score === 0) {
            feedback = 'Very weak'
            color = 'error'
        } else if (score <= 2) {
            feedback = 'Weak'
            color = 'error'
        } else if (score === 3 || score === 4) {
            feedback = 'Good'
            color = 'warning'
        } else if (score === 5) {
            feedback = 'Strong'
            color = 'success'
        }

        return { score, feedback, color }
    }

    const handlePasswordChange = (value: string) => {
        setPassword(value)
        setPasswordError('')
        setPasswordStrength(validatePasswordStrength(value))

        // Re-validate confirm password if it exists
        if (confirmPassword) {
            handleConfirmPasswordValidation(confirmPassword, value)
        }
    }

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value)
        handleConfirmPasswordValidation(value, password)
    }

    const handleConfirmPasswordValidation = (confirm: string, pwd: string) => {
        if (!confirm) {
            setConfirmError('')
            return
        }

        if (confirm !== pwd) {
            setConfirmError('Passwords do not match')
        } else {
            setConfirmError('')
        }
    }

    const handlePasswordBlur = () => {
        if (!password) {
            setPasswordError('Password is required')
            return
        }

        const unmetRequirements = passwordRequirements.filter(req => !req.met)
        if (unmetRequirements.length > 0) {
            setPasswordError('Please meet all password requirements')
            return
        }

        setPasswordError('')
    }

    const handleConfirmPasswordBlur = () => {
        handleConfirmPasswordValidation(confirmPassword, password)
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!token) {
            return
        }

        // Validate form
        if (!password || !confirmPassword) {
            if (!password) setPasswordError('Password is required')
            if (!confirmPassword) setConfirmError('Please confirm your password')
            return
        }

        if (password !== confirmPassword) {
            setConfirmError('Passwords do not match')
            return
        }

        const unmetRequirements = passwordRequirements.filter(req => !req.met)
        if (unmetRequirements.length > 0) {
            setPasswordError('Please meet all password requirements')
            return
        }

        setSubmitting(true)

        try {
            const recaptchaService = new RecaptchaService()
            const recaptchaToken = await recaptchaService.getToken().catch(() => '')

            const configuration = await getConfiguration()
            const authenticationAPI = new AuthenticationApi(configuration)

            const response = await authenticationAPI.resetPasswordWithToken({
                resetPasswordWithTokenRequest: {
                    token,
                    password,
                    recaptchaToken,
                    retrieveDeviceKeys: false
                }
            })

            // Auto-login the user after successful password reset
            if (response.authenticationResult) {
                dispatch(authenticate(response.authenticationResult))
                const alert = new AppAlert('success', 'Password reset successfully! You are now signed in.', false)
                addAlertWithTimeout(dispatch, alert)
                navigate('/')
            }
        } catch (err) {
            const error = await getErrorModel(err)

            if (!error) {
                const alert = new AppAlert('error', 'Something went wrong. Please try again later.', true)
                addAlertWithTimeout(dispatch, alert)
                setSubmitting(false)
                return
            }

            // Handle specific errors
            if (error.errors.some(x => x.message.toLowerCase().includes('token'))) {
                const alert = new AppAlert('error', 'Reset token has expired or is invalid. Please request a new password reset.', true)
                addAlertWithTimeout(dispatch, alert)
                navigate('/auth/forgot-password')
                return
            }

            const alert = new AppAlert('error', 'Unable to reset password. Please try again.', true)
            addAlertWithTimeout(dispatch, alert)
        }

        setSubmitting(false)
    }

    if (!token) {
        return null
    }

    return (
        <Box sx={{
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            py: 4
        }}>
            <Container component='div' maxWidth='sm'>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <LockResetOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                        Reset Password
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Enter your new password below
                                    </Typography>
                                </Box>
                                <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <Box>
                                            <TextField
                                                required
                                                fullWidth
                                                value={password}
                                                error={!!passwordError}
                                                helperText={passwordError}
                                                onChange={(e) => handlePasswordChange(e.target.value)}
                                                onBlur={handlePasswordBlur}
                                                type={showPassword ? 'text' : 'password'}
                                                label='New Password'
                                                autoComplete='new-password'
                                                autoFocus
                                                autoCapitalize='off'
                                                autoCorrect='off'
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    ),
                                                }}
                                            />
                                            {password && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Password strength:
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color={`${passwordStrength.color}.main`}
                                                            sx={{ fontWeight: 600 }}
                                                        >
                                                            {passwordStrength.feedback}
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={(passwordStrength.score / 5) * 100}
                                                        color={passwordStrength.color}
                                                        sx={{
                                                            height: 4,
                                                            borderRadius: 2,
                                                            bgcolor: 'action.hover'
                                                        }}
                                                    />                                                    {password && (
                                                        <Box sx={{ mt: 1.5 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                                Password requirements:
                                                            </Typography>
                                                            <Stack spacing={0.5}>
                                                                {passwordRequirements.map((requirement, index) => (
                                                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        {requirement.met ? (
                                                                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                                                        ) : (
                                                                            <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                                                        )}
                                                                        <Typography
                                                                            variant="caption"
                                                                            color={requirement.met ? 'success.main' : 'text.secondary'}
                                                                            sx={{ fontSize: '0.75rem' }}
                                                                        >
                                                                            {requirement.label}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}                                                </Box>
                                            )}
                                        </Box>
                                        <TextField
                                            required
                                            fullWidth
                                            value={confirmPassword}
                                            error={!!confirmError}
                                            helperText={confirmError}
                                            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                                            onBlur={handleConfirmPasswordBlur}
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            label='Confirm New Password'
                                            autoComplete='new-password'
                                            autoCapitalize='off'
                                            autoCorrect='off'
                                            InputProps={{
                                                endAdornment: (
                                                    <IconButton
                                                        aria-label="toggle confirm password visibility"
                                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                        edge="end"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                ),
                                            }}
                                        />
                                        <Button
                                            disabled={submitting || !!passwordError || !!confirmError || !password || !confirmPassword}
                                            type='submit'
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                        >
                                            {submitting ? 'Resetting Password...' : 'Reset Password'}
                                        </Button>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant='caption' color='text.secondary'>
                                                This site is protected by reCAPTCHA and the Google&nbsp;
                                                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</Link> and&nbsp;
                                                <Link href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</Link> apply.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Remember your password?{' '}
                                                <RouterLink to='/auth/sign-in' style={{ textDecoration: 'none' }}>
                                                    <Link component='span' sx={{ fontWeight: 600 }}>Sign in</Link>
                                                </RouterLink>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Paper>
            </Container>
        </Box>
    )
}